import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Select,
  SelectItem,
  Input,
  Switch,
  Divider,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { ReactNode, useState } from 'react';
import { toast } from 'react-toastify';

import { Settlement } from 'common/src/utils/types';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import { updateSettlement } from 'common/src/api/settlements';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface Props {
  settlement: Pick<
    Settlement,
    | 'id'
    | 'sinister_responsible'
    | 'sinister_delay_days'
    | 'sinister_case_number'
  >;
  onSave: (
    responsible: string | null,
    delayDays: number | null,
    caseNumber: string | null
  ) => void;
  /** Optional custom trigger element. Defaults to a small edit icon button. */
  trigger?: ReactNode;
}

export default function SinisterEditPopover({
  settlement,
  onSave,
  trigger,
}: Props) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [responsible, setResponsible] = useState<string>(
    settlement.sinister_responsible || ''
  );
  const [hasDelay, setHasDelay] = useState(
    (settlement.sinister_delay_days || 0) > 0
  );
  const [delayDays, setDelayDays] = useState<string>(
    settlement.sinister_delay_days ? String(settlement.sinister_delay_days) : ''
  );
  const [caseNumber, setCaseNumber] = useState<string>(
    settlement.sinister_case_number || ''
  );
  const [saving, setSaving] = useState(false);

  // Reset state from props every time the popover opens
  const handleOpenChange = (o: boolean) => {
    if (o) {
      setResponsible(settlement.sinister_responsible || '');
      const existing = settlement.sinister_delay_days || 0;
      setHasDelay(existing > 0);
      setDelayDays(existing > 0 ? String(existing) : '');
      setCaseNumber(settlement.sinister_case_number || '');
    }
    setOpen(o);
  };

  const parsedDelay = Number(delayDays);
  const delayInvalid =
    hasDelay && (!delayDays || isNaN(parsedDelay) || parsedDelay <= 0);
  const canSave = !!responsible && !delayInvalid;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const newDelay = hasDelay ? parsedDelay : null;
      const newCaseNumber = caseNumber.trim() || null;
      await updateSettlement(settlement.id, {
        sinister_responsible: responsible,
        sinister_delay_days: newDelay,
        sinister_case_number: newCaseNumber,
      });
      onSave(responsible, newDelay, newCaseNumber);
      toast.success(t('Siniestro actualizado'));
      setOpen(false);
    } catch (e: any) {
      console.error('Error updating sinister:', e);
      toast.error(e?.message || t('Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      isOpen={open}
      onOpenChange={handleOpenChange}
      placement='bottom-end'
    >
      <PopoverTrigger>
        {trigger || (
          <Button
            isIconOnly
            size='sm'
            variant='light'
            onClick={(e) => e.stopPropagation()}
            aria-label={t('Editar siniestro')}
          >
            <Icon icon='tabler:edit' className='text-default-500' />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className='p-4 w-80' onClick={(e) => e.stopPropagation()}>
        <div
          className='w-full flex flex-col gap-3'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='text-sm font-semibold flex items-center gap-2'>
            <Icon icon='tabler:edit' className='text-primary' />
            {t('Editar datos del siniestro')}
          </div>
          <Divider />
          <Select
            label={t('Responsable del siniestro')}
            placeholder={t('Selecciona un responsable')}
            selectedKeys={responsible ? [responsible] : []}
            onChange={(e) => setResponsible(e.target.value)}
            size='sm'
            isRequired
            // description='Para usos estadísticos'
          >
            {SINISTER_RESPONSIBLE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {t(opt)}
              </SelectItem>
            ))}
          </Select>
          <div className='flex items-center justify-between'>
            <div>
              <div className='text-sm font-medium'>
                {t('¿Generó retraso en la venta?')}
              </div>
            </div>
            <Switch
              isSelected={hasDelay}
              onValueChange={setHasDelay}
              size='sm'
            />
          </div>
          {hasDelay && (
            <Input
              type='number'
              min={1}
              size='sm'
              label={t('Días de retraso')}
              placeholder={t('Ej: 5')}
              value={delayDays}
              onChange={(e) => setDelayDays(e.target.value)}
              isInvalid={delayInvalid}
              errorMessage={
                delayInvalid ? t('Ingresa un número mayor a 0') : undefined
              }
            />
          )}
          <Input
            size='sm'
            label={t('Número de Caso (opcional)')}
            placeholder={t('ID interno de la exportadora')}
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
          />
          <Button
            color='primary'
            size='sm'
            onPress={handleSave}
            isLoading={saving}
            isDisabled={!canSave}
            startContent={<Icon icon='tabler:device-floppy' />}
          >
            {t('Guardar cambios')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
